import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  candidateTarget,
  importsApi,
  type CandidateType,
  type ImportDocument,
  type ImportItem,
  type ImportStage,
  type OrderImport,
} from "../../api/imports";
import { catalogApi, type ProductSummary } from "../../api/catalog";
import type { ApiError } from "../../api/client";
import Ic from "../../components/icons.jsx";
import { useToast } from "../../components/Toast";
import { useAuth } from "../../auth/AuthContext";
import { ImportStepper, type ImportStep } from "./ImportStepper";

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** How long PROCESSING may run before we surface the "taking longer" notice. */
const TAKING_LONGER_MS = 5 * 60 * 1000;

/**
 * Steps 2–4 of the import wizard. Renders the live AI-progress panel while the
 * pipeline runs (step 2), the confidence map + review tools once it lands
 * (step 3), and the hand-off to the prefilled draft order (step 4). The
 * operator can walk back through completed steps via the stepper.
 */
export default function OrderImportReviewPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  // Every endpoint under /api/orders/imports is held by orders:import — reading the
  // import included, not only finalize/resolve/add-item. Section read opens the route
  // but no call behind it, so the whole screen hangs on this one key.
  const canImport = hasPermission("orders:import");

  const [orderImport, setOrderImport] = useState<OrderImport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [finalized, setFinalized] = useState<{ orderId: string } | null>(null);
  // Stepper override: "analysis" re-opens the step-2 recap, "review" forces the map.
  const [view, setView] = useState<"auto" | "analysis" | "review">("auto");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Original-document side panel
  const [docOpen, setDocOpen] = useState(false);
  const [doc, setDoc] = useState<ImportDocument | null>(null);
  const [docError, setDocError] = useState(false);

  // Quantities tracked locally per itemId
  const [qtys, setQtys] = useState<Record<string, number>>({});

  async function load() {
    if (!id || !canImport) return;
    try {
      const data = await importsApi.get(id);
      setOrderImport(data);
      if (data.status === "FINALIZED" && data.orderId) {
        setFinalized({ orderId: data.orderId });
      }
      if (data.status !== "PROCESSING") {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch (err) {
      setError((err as ApiError).message ?? "Failed to load import");
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (orderImport?.status === "PROCESSING") {
      pollRef.current = setInterval(() => { void load(); }, 2000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderImport?.status]);

  // Seed qty defaults from orderImport items once loaded
  useEffect(() => {
    if (!orderImport) return;
    const next: Record<string, number> = {};
    for (const item of orderImport.items) {
      if (!(item.id in qtys)) {
        next[item.id] = item.resolvedQuantity ?? item.quantity ?? 1;
      }
    }
    if (Object.keys(next).length > 0) {
      setQtys((prev) => ({ ...next, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderImport]);

  async function toggleDocument() {
    if (docOpen) { setDocOpen(false); return; }
    setDocOpen(true);
    if (!id) return;
    // Refresh the presigned URL on every open — it has a short TTL.
    setDocError(false);
    try {
      setDoc(await importsApi.document(id));
    } catch {
      setDocError(true);
    }
  }

  async function resolve(
    itemId: string,
    action: "CONFIRM" | "SELECT" | "REJECT",
    target?: { id: string; type: CandidateType },
  ) {
    if (!id) return;
    const quantity = qtys[itemId];
    try {
      // SELECT carries productId; CONFIRM/REJECT need no target — CONFIRM
      // carries the matched fields over server-side.
      await importsApi.resolve(id, itemId, {
        action,
        productId: target?.type === "product" ? target.id : undefined,
        quantity,
      });
      await load();
    } catch (err) {
      setError((err as ApiError).message ?? "Resolve failed");
    }
  }

  async function finalize() {
    if (!id) return;
    setFinalizing(true);
    try {
      const result = await importsApi.finalize(id);
      setFinalized(result);
      setView("auto");
      await load();
    } catch (err) {
      // Backend exception text is not localized — show our own message for the
      // known unresolved-items case, fall back to the API message otherwise.
      const unresolved = orderImport?.items.some((it) => it.gateStatus === "REVIEW");
      setError(unresolved
        ? t("orders.import.review.unresolvedCount", { count: orderImport!.items.filter((it) => it.gateStatus === "REVIEW").length })
        : ((err as ApiError).message ?? t("orders.import.failed")));
    } finally {
      setFinalizing(false);
    }
  }

  // Section read alone lets a reader land here from a link or a bookmark. Say what is
  // missing instead of loading forever behind calls that all come back 403.
  if (!canImport) {
    return (
      <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="empty" role="status" style={{ maxWidth: 620 }}>
          {t(
            "orders.import.review.noPermission",
            "Uvoz porudžbine traži pravo upisa u sekciji Uvoz porudžbina. Vaš nalog ima samo čitanje, pa ovaj uvoz ne može ni da se otvori ni da se zaključi. Pravo dodeljuje administrator.",
          )}
        </div>
        <Link to="/orders" className="btn" style={{ textDecoration: "none", alignSelf: "flex-start" }}>
          {t("action.back")}
        </Link>
      </div>
    );
  }

  if (!orderImport && !error) {
    return <div style={{ padding: 32, color: "var(--ink-3)" }}>{t("common.loading")}</div>;
  }

  if (error && !orderImport) {
    return (
      <div style={errorCard}>
        <strong>{t("orders.import.failed")}</strong>
        <p style={{ marginTop: 8, fontSize: 13 }}>{error}</p>
      </div>
    );
  }

  if (!orderImport) return null;

  const orderId = orderImport.orderId ?? finalized?.orderId ?? null;
  // Highest fully completed step — drives which stepper steps are clickable.
  const completed = orderImport.status === "FINALIZED" ? 4 : orderImport.status === "PROCESSING" || orderImport.status === "FAILED" ? 1 : 2;
  const screen: "processing" | "failed" | "recap" | "review" | "success" =
    orderImport.status === "PROCESSING" ? "processing"
    : orderImport.status === "FAILED" ? "failed"
    : view === "analysis" ? "recap"
    : orderImport.status === "FINALIZED" && view !== "review" ? "success"
    : "review";
  const currentStep: ImportStep =
    screen === "processing" || screen === "failed" || screen === "recap" ? 2
    : screen === "review" ? 3
    : 4;

  const stepper = (
    <ImportStepper
      current={currentStep}
      completed={completed}
      orderId={orderId}
      onStepClick={(step) => setView(step === 2 ? "analysis" : "review")}
    />
  );

  if (screen === "failed") {
    return (
      <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {stepper}
        <div style={{ ...errorCard, maxWidth: 600 }}>
          <strong style={{ color: "var(--err)" }}>{t("orders.import.failed")}</strong>
          {orderImport.errorMessage && (
            <p style={{ marginTop: 8, fontSize: 13, marginBottom: 0 }}>{orderImport.errorMessage}</p>
          )}
        </div>
        <Link to="/orders" className="btn" style={{ textDecoration: "none", alignSelf: "flex-start" }}>
          {t("action.back")}
        </Link>
      </div>
    );
  }

  if (screen === "processing" || screen === "recap") {
    return (
      <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {stepper}
        <AnalysisPanel
          orderImport={orderImport}
          recap={screen === "recap"}
          onContinue={() => setView("review")}
          t={t}
        />
      </div>
    );
  }

  if (screen === "success" && orderId) {
    return (
      <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {stepper}
        <div className="card oi-panel" style={successPanel}>
          <div className="oi-success-ic" style={successCheck}><Ic.check /></div>
          <h2 style={{ margin: 0, fontSize: 20, color: "var(--ink)" }}>{t("orders.import.finalized")}</h2>
          <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink-3)", maxWidth: 420 }}>
            {t("orders.import.success.body")}
          </p>
          <Link to={`/orders/${orderId}`} className="btn primary lg" style={{ textDecoration: "none", marginTop: 6 }}>
            {t("orders.import.viewOrder")} <Ic.arrowRight />
          </Link>
        </div>
      </div>
    );
  }

  // ---- Step 3: the review map ------------------------------------------

  const items = [...orderImport.items].sort((a, b) => a.position - b.position);
  const reviewItems = items.filter((it) => it.gateStatus === "REVIEW");
  const resolvedItems = items.filter((it) => it.gateStatus !== "REVIEW");
  const hasUnresolved = reviewItems.length > 0;
  const editable = orderImport.status === "NEEDS_REVIEW" || orderImport.status === "READY";
  const canFinalize = !hasUnresolved && !finalizing && editable;
  const counts = {
    auto: items.filter((it) => it.gateStatus === "AUTO_PASS").length,
    review: reviewItems.length,
    manual: items.filter((it) => it.manuallyAdded).length,
  };
  const showPdf = doc != null && doc.contentType === "application/pdf";

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {stepper}

      {/* Page header */}
      <header style={pageHead}>
        <div>
          <h1 style={{ margin: "2px 0 2px", fontSize: 22, color: "var(--ink)" }}>
            {t("orders.import.review.title")}
          </h1>
          <div style={{ fontSize: 13, color: "var(--ink-3)" }}>
            {orderImport.originalFilename}
            {orderImport.extractionConfidence != null && (
              <span style={{ marginLeft: 10 }}>
                {/* Vocabulary is high/medium/low; anything unexpected renders raw. */}
                · {["high", "medium", "low"].includes(orderImport.extractionConfidence)
                  ? t(`orders.import.confidence.${orderImport.extractionConfidence}`)
                  : orderImport.extractionConfidence}
              </span>
            )}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn" onClick={() => void toggleDocument()}>
            <Ic.pdf />
            {docOpen ? t("orders.import.document.hide") : t("orders.import.document.show")}
          </button>
        </div>
      </header>

      {/* Confidence map summary — what the AI found, with what certainty */}
      <section>
        <h2 style={sectionTitle}>{t("orders.import.summary.title")}</h2>
        <div className="oi-summary">
          <div className="oi-summary-tile ok">
            <b>{counts.auto}</b>
            <span>{t("orders.import.summary.auto")}</span>
          </div>
          <div className={`oi-summary-tile${counts.review > 0 ? " warn" : ""}`}>
            <b>{counts.review}</b>
            <span>{t("orders.import.summary.needsReview")}</span>
          </div>
          <div className="oi-summary-tile info">
            <b>{counts.manual}</b>
            <span>{t("orders.import.summary.manual")}</span>
          </div>
        </div>
      </section>

      <div style={docOpen ? splitLayout : undefined}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
          {/* Scanned warning */}
          {orderImport.scanned && (
            <div style={warningTag}>
              <span style={{ width: 14, height: 14, flexShrink: 0, display: "inline-flex" }}><Ic.alert /></span>
              {t("orders.import.scannedWarning")}
            </div>
          )}

          {/* Extractor ambiguities */}
          {orderImport.ambiguities && orderImport.ambiguities.length > 0 && (
            <div style={ambigBox}>
              {orderImport.ambiguities.map((a, i) => (
                <div key={i} style={{ fontSize: 12.5, color: "var(--ink-3)" }}>— {a}</div>
              ))}
            </div>
          )}

          {/* API-level error inline */}
          {error && (
            <div style={errorCard}>
              <strong>{error}</strong>
            </div>
          )}

          {/* REVIEW items */}
          {reviewItems.length > 0 && (
            <section id="oi-review-section">
              <h2 style={sectionTitle}>
                {t("orders.import.review.needsReview")}
                <span style={countBadge}>{reviewItems.length}</span>
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {reviewItems.map((item) => (
                  <ReviewCard
                    key={item.id}
                    item={item}
                    qty={qtys[item.id] ?? item.quantity ?? 1}
                    onQtyChange={(v) => setQtys((prev) => ({ ...prev, [item.id]: v }))}
                    onConfirm={() => resolve(item.id, "CONFIRM")}
                    onSelect={(target) => resolve(item.id, "SELECT", target)}
                    onReject={() => resolve(item.id, "REJECT")}
                    locale={i18n.language?.slice(0, 2) ?? "en"}
                    t={t}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Auto-passed / resolved table — each row with its confidence and source */}
          {resolvedItems.length > 0 && (
            <section>
              <h2 style={sectionTitle}>
                {t("orders.import.review.autoPassed")}
                <span style={countBadge}>{resolvedItems.length}</span>
              </h2>
              <div className="card" style={{ overflow: "hidden", padding: 0 }}>
                <table style={tbl}>
                  <thead>
                    <tr>
                      <th style={th}>#</th>
                      <th style={th}>{t("orders.import.review.customerWrote")}</th>
                      <th style={th}>SKU</th>
                      <th style={{ ...th, textAlign: "right" }}>{t("orders.import.review.quantity")}</th>
                      <th style={th}>{t("orders.import.review.suggestion")}</th>
                      <th style={th}>{t("orders.import.review.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resolvedItems.map((item) => {
                      const display = resolvedDisplay(item);
                      return (
                        <tr key={item.id} style={rowStyle}>
                          <td style={td}>{item.position}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 500, color: "var(--ink)" }}>{item.rawName}</div>
                            {item.rawCode && (
                              <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{item.rawCode}</div>
                            )}
                          </td>
                          <td style={td}>
                            {display.sku ? (
                              <div>
                                <code style={mono}>{display.sku}</code>
                                {display.name && (
                                  <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{display.name}</div>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: "var(--ink-4)" }}>—</span>
                            )}
                          </td>
                          <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {item.resolvedQuantity ?? item.quantity ?? "—"}
                            {item.unit && <span style={{ color: "var(--ink-3)", fontSize: 11.5, marginLeft: 4 }}>{item.unit}</span>}
                          </td>
                          <td style={td}>
                            {item.matchConfidence != null && (
                              <span className={`tag ${confidenceTone(item.matchConfidence)}`}>
                                {t(`orders.import.confidence.${item.matchConfidence}`)}
                              </span>
                            )}
                            {item.matchReason && (
                              <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 3 }}>{item.matchReason}</div>
                            )}
                          </td>
                          <td style={td}>
                            <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                              <GateBadge status={item.gateStatus} t={t} />
                              {item.manuallyAdded && (
                                <span className="tag info">{t("orders.import.manualTag")}</span>
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ADD ROW — a line the AI missed */}
          {editable && (
            <AddRowPanel
              importId={orderImport.id}
              locale={i18n.language?.slice(0, 2) ?? "en"}
              onAdded={() => void load()}
              onError={(msg) => setError(msg)}
              t={t}
            />
          )}

          {/* Finalize / step-4 hand-off */}
          <div style={finalizeRow}>
            {orderImport.status === "FINALIZED" && orderId ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ color: "var(--ok)", fontWeight: 600 }}>{t("orders.import.finalized")}</span>
                <Link to={`/orders/${orderId}`} className="btn primary" style={{ textDecoration: "none" }}>
                  {t("orders.import.viewOrder")} <Ic.arrowRight />
                </Link>
              </div>
            ) : (
              <span
                // Disabled buttons swallow clicks — catch them on the wrapper and
                // guide the operator to the items that still block finalize.
                onClick={() => {
                  if (hasUnresolved && !finalizing) {
                    toast.info(t("orders.import.review.unresolvedCount", { count: reviewItems.length }));
                    document.getElementById("oi-review-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
                style={{ display: "inline-flex", cursor: hasUnresolved ? "pointer" : undefined }}
              >
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => void finalize()}
                  disabled={!canFinalize}
                >
                  {finalizing ? t("common.loading") : t("orders.import.finalize")}
                </button>
              </span>
            )}
            {hasUnresolved && orderImport.status !== "FINALIZED" && (
              <span className="tag warn" style={{ fontSize: 12.5 }}>
                <Ic.alert /> {t("orders.import.review.unresolvedCount", { count: reviewItems.length })}
              </span>
            )}
          </div>
        </div>

        {/* Original-document side panel */}
        {docOpen && (
          <DocumentPanel
            doc={doc}
            docError={docError}
            showPdf={showPdf}
            filename={orderImport.originalFilename}
            onClose={() => setDocOpen(false)}
            t={t}
          />
        )}
      </div>
    </div>
  );
}

// ---- Step 2: live AI progress / recap -----------------------------------

const STAGE_ORDER: ImportStage[] = ["EXTRACTING", "ANALYZING", "MATCHING", "GATING"];
const STAGE_LABEL_KEY: Record<string, string> = {
  EXTRACTING: "orders.import.stage.reading",
  ANALYZING: "orders.import.stage.extracting",
  MATCHING: "orders.import.stage.matching",
  GATING: "orders.import.stage.gating",
};

function AnalysisPanel({
  orderImport,
  recap,
  onContinue,
  t,
}: {
  orderImport: OrderImport;
  recap: boolean;
  onContinue: () => void;
  t: TFn;
}) {
  // Stage null means the pipeline thread hasn't reported yet — show stage 1 active.
  const activeIdx = recap ? STAGE_ORDER.length : Math.max(0, STAGE_ORDER.indexOf(orderImport.stage ?? "EXTRACTING"));
  const takingLonger = !recap && Date.now() - new Date(orderImport.createdAt).getTime() > TAKING_LONGER_MS;

  return (
    <div className="card oi-panel">
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--ink)" }}>
          {recap ? t("orders.import.stage.done") : t("orders.import.processing")}
        </h2>
        <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 4, display: "inline-flex", alignItems: "center", gap: 8 }}>
          {!recap && <span className="oi-spinner" aria-hidden />}
          {orderImport.originalFilename}
        </div>
      </div>

      <div role="status" aria-live="polite">
        {STAGE_ORDER.map((stage, i) => {
          const state = i < activeIdx ? "done" : i === activeIdx ? "active" : "pending";
          const isMatching = stage === "MATCHING";
          const showProgress = isMatching && state === "active" && orderImport.progressTotal > 0;
          const label = showProgress
            ? t("orders.import.stage.matchingProgress", { done: Math.min(orderImport.progressDone + 1, orderImport.progressTotal), total: orderImport.progressTotal })
            : t(STAGE_LABEL_KEY[stage]);
          return (
            <div key={stage} className={`oi-stage-row ${state}`}>
              <span className="oi-stage-ic">
                {state === "done" ? <Ic.check /> : <span style={{ fontSize: 10.5, fontWeight: 700 }}>{i + 1}</span>}
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="oi-stage-label">{label}</div>
                {showProgress && (
                  <div className="oi-progress-track">
                    <div
                      className="oi-progress-fill"
                      style={{ width: `${Math.round((orderImport.progressDone / orderImport.progressTotal) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {recap && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 18 }}>
          <button type="button" className="btn primary" onClick={onContinue}>
            {t("orders.import.recap.continue")} <Ic.arrowRight />
          </button>
        </div>
      )}

      {takingLonger && (
        <div style={{ ...warningTag, display: "flex", marginTop: 18 }}>
          <span style={{ width: 14, height: 14, flexShrink: 0, display: "inline-flex" }}><Ic.alert /></span>
          {t("orders.import.takingLonger")}
        </div>
      )}
    </div>
  );
}

// ---- Step 3 helpers ------------------------------------------------------

/**
 * Display SKU/name for a resolved row. After SELECT the resolved target can
 * differ from the original suggestion — look it up in the candidates payload.
 */
function resolvedDisplay(item: ImportItem): { sku: string | null; name: string | null } {
  const resolvedId = item.resolvedProductId;
  const matchedId = item.matchedProductId;
  if (resolvedId && resolvedId !== matchedId) {
    const c = (item.candidates ?? []).find((cand) => (cand.id ?? cand.productId) === resolvedId);
    if (c) return { sku: c.sku, name: c.name };
  }
  return { sku: item.matchedSku, name: item.matchedName };
}

function confidenceTone(confidence: "high" | "medium" | "low"): string {
  return confidence === "high" ? "ok" : confidence === "medium" ? "warn" : "err";
}

function DocumentPanel({
  doc,
  docError,
  showPdf,
  filename,
  onClose,
  t,
}: {
  doc: ImportDocument | null;
  docError: boolean;
  showPdf: boolean;
  filename: string;
  onClose: () => void;
  t: TFn;
}) {
  return (
    <aside className="card" style={docPanel}>
      <div className="card-h" style={{ padding: "10px 14px" }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {t("orders.import.document.show")}
        </h3>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {doc && (
            <a className="btn sm" href={doc.url} download={doc.filename} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <Ic.download /> {t("orders.import.document.download")}
            </a>
          )}
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label={t("orders.import.document.hide")}>
            <Ic.x />
          </button>
        </div>
      </div>
      {docError ? (
        <div style={{ ...errorCard, margin: 14 }}>{t("orders.import.document.error")}</div>
      ) : !doc ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
          {t("common.loading")}
        </div>
      ) : showPdf ? (
        <iframe className="oi-doc-frame" src={doc.url} title={doc.filename} />
      ) : (
        <div style={{ padding: 28, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <span className="oi-doc-ic" style={{ color: "var(--ink-3)", display: "inline-flex" }}><Ic.pdf /></span>
          <code style={{ ...mono, wordBreak: "break-all", textAlign: "center" }}>{filename}</code>
          <a className="btn primary" href={doc.url} download={doc.filename} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
            <Ic.download /> {t("orders.import.document.download")}
          </a>
        </div>
      )}
    </aside>
  );
}

/** Inline "add a row the AI missed" control: debounced product search + qty. */
function AddRowPanel({
  importId,
  locale,
  onAdded,
  onError,
  t,
}: {
  importId: string;
  locale: string;
  onAdded: () => void;
  onError: (msg: string) => void;
  t: TFn;
}) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ProductSummary[] | null>(null);
  const [picked, setPicked] = useState<ProductSummary | null>(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults(null); return; }
    let live = true;
    const timer = setTimeout(() => {
      void catalogApi.searchProducts(q, locale, 10)
        .then((r) => { if (live) setResults(r); })
        .catch(() => { if (live) setResults([]); });
    }, 300);
    return () => { live = false; clearTimeout(timer); };
  }, [term, locale]);

  async function add() {
    if (!picked || busy) return;
    setBusy(true);
    try {
      await importsApi.addItem(importId, { productId: picked.id, quantity: qty });
      setPicked(null);
      setTerm("");
      setQty(1);
      setResults(null);
      onAdded();
    } catch (err) {
      onError((err as ApiError).message ?? "Add failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" style={{ padding: "16px 18px" }}>
      <h3 style={{ ...sectionTitle, marginBottom: 10 }}>{t("orders.import.addRow.title")}</h3>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto auto", gap: 8, alignItems: "center" }}>
        <div style={{ position: "relative" }}>
          <input
            className="input"
            placeholder={t("orders.import.addRow.searchPlaceholder")}
            value={picked ? `${picked.sku} · ${picked.name}` : term}
            onChange={(e) => { setTerm(e.target.value); setPicked(null); }}
            aria-label={t("orders.import.addRow.searchPlaceholder")}
          />
          {!picked && results != null && (
            <div className="dd-scroll" style={typeaheadPanel}>
              {results.length === 0 ? (
                <div style={{ padding: "10px 12px", fontSize: 12.5, color: "var(--ink-3)" }}>
                  {t("orders.import.addRow.noResults")}
                </div>
              ) : (
                results.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => { setPicked(p); setTerm(""); setResults(null); }}
                    style={typeaheadOption}
                  >
                    <code style={mono}>{p.sku}</code>
                    <span style={{ flex: 1, minWidth: 0 }}>{p.name}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <input
          type="number"
          min={1}
          value={qty}
          onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
          className="input"
          style={{ width: 80, textAlign: "right" }}
          aria-label={t("orders.import.review.quantity")}
        />
        <button
          type="button"
          className="btn primary"
          onClick={() => void add()}
          disabled={!picked || busy || qty < 1}
        >
          <Ic.plus /> {t("orders.import.addRow.add")}
        </button>
      </div>
    </section>
  );
}

// Sub-component: card for a single REVIEW item
/**
 * Inline catalog search on a review card: lets the operator assign ANY
 * published product when the AI suggestion/candidates are wrong or missing.
 * Same debounced-typeahead pattern as AddRowPanel; picking calls the regular
 * SELECT resolve path (backend accepts any productId, not just candidates).
 */
function ManualAssignSearch({
  locale,
  disabled,
  onPick,
  t,
}: {
  locale: string;
  disabled: boolean;
  onPick: (productId: string) => void;
  t: TFn;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ProductSummary[] | null>(null);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults(null); return; }
    let live = true;
    const timer = setTimeout(() => {
      void catalogApi.searchProducts(q, locale, 10)
        .then((r) => { if (live) setResults(r); })
        .catch(() => { if (live) setResults([]); });
    }, 300);
    return () => { live = false; clearTimeout(timer); };
  }, [term, locale]);

  if (!open) {
    return (
      <button type="button" className="btn sm" disabled={disabled} onClick={() => setOpen(true)}>
        <Ic.search /> {t("orders.import.review.assignManually")}
      </button>
    );
  }
  return (
    <div style={{ position: "relative", flex: "1 1 240px", minWidth: 220 }}>
      <input
        className="input"
        autoFocus
        placeholder={t("orders.import.addRow.searchPlaceholder")}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); setTerm(""); setResults(null); } }}
        aria-label={t("orders.import.review.assignManually")}
        style={{ width: "100%" }}
      />
      {results != null && (
        <div className="dd-scroll" style={typeaheadPanel}>
          {results.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 12.5, color: "var(--ink-3)" }}>
              {t("orders.import.addRow.noResults")}
            </div>
          ) : (
            results.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => { onPick(p.id); setOpen(false); setTerm(""); setResults(null); }}
                style={typeaheadOption}
              >
                <code style={mono}>{p.sku}</code>
                <span style={{ flex: 1, minWidth: 0 }}>{p.name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ReviewCard({
  item,
  qty,
  onQtyChange,
  onConfirm,
  onSelect,
  onReject,
  locale,
  t,
}: {
  item: ImportItem;
  qty: number;
  onQtyChange: (v: number) => void;
  onConfirm: () => void;
  onSelect: (target: { id: string; type: CandidateType }) => void;
  onReject: () => void;
  locale: string;
  t: TFn;
}) {
  const matchedId = item.matchedProductId;
  return (
    <div className="card" style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Customer wrote */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 200px" }}>
          <div style={miniLabel}>{t("orders.import.review.customerWrote")}</div>
          <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 14 }}>{item.rawName}</div>
          {item.rawCode && (
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
              <code style={mono}>{item.rawCode}</code>
            </div>
          )}
          {item.contextNote && (
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2, fontStyle: "italic" }}>
              {item.contextNote}
            </div>
          )}
        </div>

        {/* Suggested match */}
        {matchedId && (
          <div style={{ flex: "1 1 200px" }}>
            <div style={miniLabel}>{t("orders.import.review.suggestion")}</div>
            <div style={{ fontWeight: 500, fontSize: 13, color: "var(--ink)" }}>
              {item.matchedSku && <code style={{ ...mono, marginRight: 6 }}>{item.matchedSku}</code>}
              {item.matchedName}
            </div>
            {item.matchConfidence != null && (
              <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span className={`tag ${confidenceTone(item.matchConfidence)}`}>
                  {t(`orders.import.confidence.${item.matchConfidence}`)}
                </span>
                {item.matchReason && <span>{item.matchReason}</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Violations */}
      {item.violations.length > 0 && (
        <div>
          <div style={miniLabel}>{t("orders.import.review.flagReason")}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {item.violations.map((v) => (
              <span key={v} className="tag err">
                {t(`orders.import.violation.${v}`) || v}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Attribute conflicts */}
      {item.attributeCheck && Object.keys(item.attributeCheck).length > 0 && (
        <div style={attrConflictBox}>
          {Object.entries(item.attributeCheck).map(([k, v]) => (
            <div key={k}><b>{k}:</b> {v}</div>
          ))}
        </div>
      )}

      {/* Quantity + actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)" }} htmlFor={`qty-${item.id}`}>
            {t("orders.import.review.quantity")}
          </label>
          <input
            id={`qty-${item.id}`}
            type="number"
            min={1}
            value={qty}
            onChange={(e) => onQtyChange(Math.max(1, Number(e.target.value)))}
            className="input"
            style={{ width: 80, height: 32, textAlign: "right" }}
          />
          {item.unit && <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{item.unit}</span>}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Confirm primary match */}
          {matchedId && (
            <button
              type="button"
              className="btn primary sm"
              onClick={onConfirm}
              disabled={!qty || qty < 1}
            >
              <Ic.check /> {t("orders.import.review.confirm")}
            </button>
          )}

          {/* Candidates — candidateTarget() normalizes {id,type} and older {productId} rows */}
          {(() => {
            const alternates: Array<{ sku: string; name: string; sim: number; target: { id: string; type: CandidateType } }> = [];
            for (const c of item.candidates ?? []) {
              const target = candidateTarget(c);
              if (target && target.id !== matchedId) {
                alternates.push({ sku: c.sku, name: c.name, sim: c.sim, target });
              }
            }
            if (alternates.length === 0) return null;
            return (
              <>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--ink-4)", alignSelf: "center" }}>
                  {t("orders.import.review.candidates")}:
                </span>
                {alternates.map((c) => (
                  <button
                    key={c.target.id}
                    type="button"
                    className="btn sm"
                    onClick={() => onSelect(c.target)}
                    disabled={!qty || qty < 1}
                    title={`${c.name} (sim: ${c.sim})`}
                  >
                    {t("orders.import.review.choose")}: {c.sku}
                  </button>
                ))}
              </>
            );
          })()}

          {/* Manual assignment — any catalog product, not just AI candidates */}
          <ManualAssignSearch
            locale={locale}
            disabled={!qty || qty < 1}
            onPick={(productId) => onSelect({ id: productId, type: "product" })}
            t={t}
          />

          {/* Reject */}
          <button
            type="button"
            className="btn sm"
            style={rejectBtn}
            onClick={onReject}
          >
            <Ic.x /> {t("orders.import.review.reject")}
          </button>
        </div>
      </div>
    </div>
  );
}

function GateBadge({ status, t }: { status: string; t: TFn }) {
  const tone: Record<string, string> = {
    AUTO_PASS: "ok",
    CONFIRMED: "info",
    REJECTED: "err",
  };
  const label = t(`orders.import.gate.${status}`) || status;
  return <span className={`tag ${tone[status] ?? "neutral"}`}>{label}</span>;
}

const pageHead: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" };
const sectionTitle: CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: "var(--ink-2)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  marginBottom: 10,
  marginTop: 0,
  display: "flex",
  alignItems: "center",
  gap: 8,
};
const countBadge: CSSProperties = {
  background: "var(--brand-primary)",
  color: "var(--on-primary)",
  borderRadius: 999,
  padding: "1px 8px",
  fontSize: 11,
  fontWeight: 700,
};
const warningTag: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  background: "var(--warn-bg)",
  border: "1px solid var(--warn)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--warn)",
  fontWeight: 500,
};
const ambigBox: CSSProperties = {
  padding: "10px 14px",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 8,
};
const errorCard: CSSProperties = {
  padding: "14px 18px",
  background: "var(--err-bg)",
  border: "1px solid var(--err)",
  borderRadius: 10,
  color: "var(--err)",
  fontSize: 14,
};
const attrConflictBox: CSSProperties = {
  fontSize: 12,
  color: "var(--warn)",
  background: "var(--warn-bg)",
  border: "1px solid var(--warn)",
  borderRadius: 6,
  padding: "6px 10px",
};
const miniLabel: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--ink-4)",
  marginBottom: 4,
};
const rejectBtn: CSSProperties = {
  color: "var(--err)",
  borderColor: "var(--err)",
};
const finalizeRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "16px 0",
  borderTop: "1px solid var(--border)",
  flexWrap: "wrap",
};
const tbl: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  background: "var(--surface-2)",
  color: "var(--ink-3)",
  fontWeight: 600,
  fontSize: 11.5,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: "1px solid var(--border)",
};
const td: CSSProperties = { padding: "11px 12px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const rowStyle: CSSProperties = { transition: "background .12s ease" };
const mono: CSSProperties = { fontFamily: "var(--mono)", fontSize: 12.5, color: "var(--ink-2)" };
const splitLayout: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(320px, 42%)",
  gap: 20,
  alignItems: "start",
};
const docPanel: CSSProperties = {
  position: "sticky",
  top: 84,
  overflow: "hidden",
};
const successPanel: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
  textAlign: "center",
};
const successCheck: CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  background: "var(--ok-bg)",
  color: "var(--ok)",
  display: "grid",
  placeItems: "center",
};
const typeaheadPanel: CSSProperties = {
  position: "absolute",
  zIndex: 20,
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)",
  boxShadow: "var(--shadow-2)",
  maxHeight: 280,
};
const typeaheadOption: CSSProperties = {
  display: "flex",
  gap: 12,
  alignItems: "center",
  padding: "10px 12px",
  width: "100%",
  textAlign: "left",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: 13,
  color: "var(--ink)",
  borderBottom: "1px solid var(--border)",
};
